import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useRestApi } from 'context/RestApiContext';
import { MessageModal } from 'components/modal/MessageModal';

/**
 * 期限切れメッセージマスタ管理モーダル
 * チェックリスト風UI: クリックでインライン編集、ホバーで削除ボタン表示
 */
function ExpiredMessageMasterModal({ show, onClose, onUpdated }) {
  const restApi = useRestApi();
  const [messages, setMessages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newText, setNewText] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [dialog, setDialog] = useState({});

  // マスタ一覧を取得
  const fetchMessages = () => {
    restApi.get('/api/expired_messages', (data) => {
      setMessages(data?.messages ?? []);
    });
  };

  useEffect(() => {
    if (show) {
      fetchMessages();
      setEditingId(null);
      setNewText('');
    }
  }, [show]);

  // 追加
  const handleAdd = () => {
    if (!newText.trim()) return;
    restApi.post('/api/expired_messages', { MESSAGE_TEXT: newText.trim() }, (_) => {
      setNewText('');
      fetchMessages();
      onUpdated?.();
    });
  };

  // テキスト変更（ローカルstateのみ更新）
  const handleChangeText = (messageId) => (e) => {
    const v = e?.target?.value ?? '';
    setMessages((prev) =>
      prev.map((m) => (m.MESSAGE_ID === messageId ? { ...m, MESSAGE_TEXT: v } : m))
    );
  };

  // 編集開始
  const handleStartEdit = (id) => setEditingId(id);

  // 編集確定（blur / Enter）
  const handleCommitEdit = (id) => {
    setEditingId(null);
    const target = messages.find((m) => m.MESSAGE_ID === id);
    if (!target || !target.MESSAGE_TEXT.trim()) return;
    restApi.post(`/api/expired_messages/${id}`, { MESSAGE_TEXT: target.MESSAGE_TEXT.trim() }, (_) => {
      fetchMessages();
      onUpdated?.();
    });
  };

  // 削除
  const handleDelete = (messageId) => {
    restApi.post(`/api/expired_messages/${messageId}/delete`, {}, (_) => {
      fetchMessages();
      onUpdated?.();
    });
  };

  return (
    <Dialog fullWidth maxWidth="md" open={show} onClose={onClose}>
      <DialogTitle sx={{ backgroundColor: '#f5f5f5' }}>期限切れメッセージ管理</DialogTitle>
      <DialogContent sx={{ backgroundColor: '#f5f5f5' }}>
        <List dense sx={{ m: 0, p: 0 }}>
          {messages.map((msg) => (
            <ListItem
              key={msg.MESSAGE_ID}
              sx={{
                bgcolor: 'inherit',
                borderRadius: 1,
                mb: 0, mt: 0, py: 0,
                minHeight: 'auto',
                '&:hover': { bgcolor: '#e8e8e8' },
              }}
            >
              <Box sx={{ m: 0.5, flex: 1 }}>
                {editingId === msg.MESSAGE_ID ? (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={msg.MESSAGE_TEXT}
                    onChange={handleChangeText(msg.MESSAGE_ID)}
                    onBlur={() => handleCommitEdit(msg.MESSAGE_ID)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        handleCommitEdit(msg.MESSAGE_ID);
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                    }}
                    autoFocus
                  />
                ) : (
                  <span
                    onClick={() => handleStartEdit(msg.MESSAGE_ID)}
                    onMouseEnter={() => setHoveredId(msg.MESSAGE_ID)}
                    onMouseLeave={() => setHoveredId(null)}
                    style={{
                      cursor: 'text',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 6px',
                      width: '100%',
                      gap: '6px',
                    }}
                  >・
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {msg.MESSAGE_TEXT}
                    </span>
                    <IconButton
                      size="small"
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(msg.MESSAGE_ID);
                      }}
                      sx={{
                        ml: 'auto',
                        p: '2px',
                        visibility: hoveredId === msg.MESSAGE_ID ? 'visible' : 'hidden',
                        pointerEvents: hoveredId === msg.MESSAGE_ID ? 'auto' : 'none',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                )}
              </Box>
            </ListItem>
          ))}
        </List>

        {messages.length === 0 && (
          <div className="text-center text-muted py-2">メッセージパターンがありません</div>
        )}

        <div className="mt-1" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <TextField
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="メッセージを入力して 追加 を押下"
            multiline
            rows={3}
            size="small"
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
            }}
          />
          <Button
            style={{ height: '40px', whiteSpace: 'nowrap' }}
            disabled={!newText.trim()}
            variant="primary"
            className="btn-fill"
            onClick={handleAdd}
          >
            追加
          </Button>
        </div>
      </DialogContent>
      <DialogActions className="p-2" sx={{ backgroundColor: '#f5f5f5' }}>
        <Button variant="secondary" className="footer-button" onClick={onClose}>
          閉じる
        </Button>
      </DialogActions>
      <MessageModal dialog={dialog} />
    </Dialog>
  );
}

export default ExpiredMessageMasterModal;
